import { FileText, Package, Clock, Warning, User as UserIcon, UsersThree, Briefcase } from "@phosphor-icons/react";
import OpenInTabLink from "../common/OpenInTabLink";

// Deals Board v2 — Compact, hierarchical card with ownership + aging + activity.
// Visual hierarchy (top → bottom):
//  1. Title + value (primary)
//  2. Ownership row: Created By · Lead Owner · Assigned BDE
//  3. Meta row: aging chip · last activity · follow-up overdue badge
//  4. Stage select + quotation/SO badges

const fmtINR = (v) => `₹${(Number(v) || 0).toLocaleString("en-IN")}`;
const fmtAgo = (iso) => {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "—";
  const days = Math.floor((Date.now() - t) / (1000 * 60 * 60 * 24));
  if (days < 0) return "future";
  if (days === 0) return "today";
  if (days === 1) return "1d ago";
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
};

const STAGES = [
  { key: "prospect", label: "Prospect" },
  { key: "negotiation", label: "Negotiation" },
  { key: "proposal", label: "Proposal" },
  { key: "sales_order_created", label: "Sales Order Created" },
  { key: "closed_won", label: "Closed Won" },
  { key: "closed_lost", label: "Closed Lost" },
];

export default function DealCard({
  deal,
  index,
  stageKey,
  stageColor,
  canEdit,
  canDelete,
  quotingDealId,
  onMoveStage,
  onCreateQuotation,
  onDelete,
}) {
  const aging = deal._aging;
  const followupOverdue = deal._followupOverdue;
  const isTerminal = stageKey === "closed_won" || stageKey === "closed_lost";

  // Ownership hierarchy — STRICT separation. customer_name MUST NEVER appear in owner fields.
  const customerName = deal.contact_name || "—";
  const createdBy = deal.created_by_name || "—";
  const createdByRole = (deal.created_by_role || "").toUpperCase();
  const leadOwnerName = (deal.lead_owner_name || "").trim();
  const currentBdeName = (deal.assigned_to || "").trim();
  const currentBdeRole = (deal.assigned_to_role || "").toUpperCase();
  // Merge-when-same logic: if both present and identical → single merged row.
  // If they differ → two separate rows for managerial clarity. If neither → "Unassigned".
  const ownerMode = (() => {
    if (!leadOwnerName && !currentBdeName) return "unassigned";
    if (leadOwnerName && currentBdeName && leadOwnerName === currentBdeName) return "merged";
    if (leadOwnerName && currentBdeName) return "split";
    if (leadOwnerName) return "merged"; // only lead owner known — show as Lead Owner/BDE merged
    return "merged"; // only BDE known
  })();

  return (
    <div
      data-testid={`pipeline-deal-${stageKey}-${index}`}
      data-deal-id={deal.deal_id}
      className="bg-white border border-slate-200 rounded-none p-3 hover:shadow-md transition-shadow duration-200"
      style={{ borderLeft: `3px solid ${stageColor}` }}
    >
      {/* Row 1 — Title + value */}
      <div className="flex items-start justify-between gap-2">
        <h5 className="font-semibold text-sm leading-snug truncate flex items-center gap-1 min-w-0" title={deal.title}>
          <a
            href={`/crm/deals/${deal.deal_id}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="hover:underline hover:text-[#002FA7] truncate"
            data-testid={`deal-title-link-${deal.deal_id}`}
          >
            {deal.title}
          </a>
          <OpenInTabLink to={`/crm/deals/${deal.deal_id}`} testId={`deal-open-tab-${deal.deal_id}`} size={11} />
        </h5>
        <span className="text-sm font-bold whitespace-nowrap" style={{ color: stageColor }}>
          {fmtINR(deal.value)}
        </span>
      </div>
      <p className="text-[11px] text-slate-500 mt-0.5 truncate">
        {deal.probability}% probability
      </p>

      {/* Row 2 — Ownership hierarchy: Customer → Lead Owner/BDE (merged or split) → Created by */}
      <div className="mt-2 space-y-1 text-[11px]">
        <div data-testid="deal-customer" className="flex items-center gap-1 text-slate-600 truncate" title={`Customer ${customerName}`}>
          <UsersThree size={11} weight="bold" className="text-slate-400 shrink-0" />
          <span className="font-medium">Customer:</span>
          <span className="truncate">{customerName}</span>
        </div>
        {ownerMode === "split" ? (
          <>
            <div data-testid="deal-lead-owner" className="flex items-center gap-1 text-slate-600 truncate" title={`Lead Owner ${leadOwnerName}`}>
              <UserIcon size={11} weight="bold" className="text-slate-400 shrink-0" />
              <span className="font-medium">Lead Owner:</span>
              <span className="truncate">{leadOwnerName}</span>
            </div>
            <div data-testid="deal-current-bde" className="flex items-center gap-1 text-slate-600 truncate" title={`Current BDE ${currentBdeName}`}>
              <Briefcase size={11} weight="bold" className="text-slate-400 shrink-0" />
              <span className="font-medium">Current BDE:</span>
              <span className="truncate">{currentBdeName}</span>
              {currentBdeRole && <span className="text-slate-400">· {currentBdeRole}</span>}
            </div>
          </>
        ) : ownerMode === "unassigned" ? (
          <div data-testid="deal-lead-owner-bde" className="flex items-center gap-1 text-slate-400 truncate">
            <Briefcase size={11} weight="bold" className="text-slate-400 shrink-0" />
            <span className="font-medium">Lead Owner / BDE:</span>
            <span className="italic">Unassigned</span>
          </div>
        ) : (
          <div data-testid="deal-lead-owner-bde" className="flex items-center gap-1 text-slate-600 truncate" title={`Lead Owner / BDE ${leadOwnerName || currentBdeName}`}>
            <Briefcase size={11} weight="bold" className="text-slate-400 shrink-0" />
            <span className="font-medium">Lead Owner / BDE:</span>
            <span className="truncate">{leadOwnerName || currentBdeName}</span>
            {currentBdeRole && <span className="text-slate-400">· {currentBdeRole}</span>}
          </div>
        )}
        <div className="flex items-center gap-1 text-slate-500 truncate" title={`Created by ${createdBy}`}>
          <UserIcon size={11} weight="bold" className="text-slate-300 shrink-0" />
          <span className="font-medium">Created by:</span>
          <span className="truncate">{createdBy}</span>
          {createdByRole && <span className="text-slate-400">· {createdByRole}</span>}
        </div>
      </div>

      {/* Row 3 — Aging + last activity + overdue */}
      <div className="flex items-center flex-wrap gap-1 mt-2">
        {aging && (
          <span
            data-testid={`deal-aging-${aging.key}`}
            className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 font-bold rounded-none"
            style={{ backgroundColor: `${aging.color}15`, color: aging.color, border: `1px solid ${aging.color}40` }}
            title={`${aging.label} · ${aging.days}d since last activity`}
          >
            {aging.label} · {aging.days}d
          </span>
        )}
        {followupOverdue && (
          <span
            data-testid={`deal-followup-overdue`}
            className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 font-bold rounded-none border inline-flex items-center gap-1"
            style={{ backgroundColor: "#DC262615", color: "#DC2626", borderColor: "#DC262640" }}
            title="Expected close date has passed"
          >
            <Warning size={10} weight="bold" /> Overdue
          </span>
        )}
        <span className="text-[10px] text-slate-500 inline-flex items-center gap-0.5 ml-auto" title={`Last activity: ${deal._activityIso || "unknown"}`}>
          <Clock size={10} /> {fmtAgo(deal._activityIso)}
        </span>
      </div>

      {/* Row 4 — Stage move + Quotation/SO badges */}
      {!isTerminal && canEdit && (
        <select
          data-testid={`move-deal-${stageKey}-${index}`}
          value={deal.stage}
          onChange={(e) => onMoveStage(deal.deal_id, e.target.value)}
          className="rounded-none border border-slate-300 text-xs p-1 outline-none w-full mt-2 bg-white"
        >
          {STAGES.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>
      )}

      <div className="flex items-center gap-1 mt-2">
        {deal.linked_quotation_id ? (
          <button
            data-testid={`deal-view-quote-${stageKey}-${index}`}
            onClick={() => onCreateQuotation(deal)}
            className="text-[10px] uppercase tracking-wider px-2 py-1 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-bold inline-flex items-center gap-1 flex-1"
            title={`Linked: ${deal.linked_quotation_number} · ${deal.quotation_status}`}
          >
            <FileText size={11} weight="bold" /> {deal.linked_quotation_number}
            <span className="ml-auto opacity-70 normal-case">{deal.quotation_status || "draft"}</span>
          </button>
        ) : (
          <button
            data-testid={`deal-create-quote-${stageKey}-${index}`}
            disabled={quotingDealId === deal.deal_id}
            onClick={() => onCreateQuotation(deal)}
            className="text-[10px] uppercase tracking-wider px-2 py-1 border border-blue-300 text-blue-700 hover:bg-blue-50 font-bold inline-flex items-center gap-1 disabled:opacity-50 flex-1"
          >
            <FileText size={11} weight="bold" /> {quotingDealId === deal.deal_id ? "Creating…" : "Quotation"}
          </button>
        )}
        {canDelete && (
          <button
            data-testid={`delete-deal-${stageKey}-${index}`}
            onClick={() => onDelete(deal.deal_id)}
            className="text-[10px] uppercase tracking-wider px-2 py-1 border border-red-300 text-red-700 hover:bg-red-50 font-bold"
            title="Delete deal"
          >
            Del
          </button>
        )}
      </div>

      {deal.linked_so_id && (
        <a
          data-testid={`deal-so-badge-${stageKey}-${index}`}
          href="/billing"
          className="text-[10px] uppercase tracking-wider px-2 py-1 border border-violet-300 text-violet-700 hover:bg-violet-50 font-bold inline-flex items-center gap-1 mt-1 w-full justify-center"
          title={`Linked Sales Order: ${deal.linked_so_number}`}
        >
          <Package size={11} weight="bold" /> {deal.linked_so_number}
        </a>
      )}
    </div>
  );
}
